// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the flat 2:1 map (owner, 2026-10-19; docs/AYNI_XR_FLAT_MAP_PLAN.md):
 * the same MapLibre map as the globe, switched to a registered planar
 * projection, plate carrée (equirectangular), with `map.setProjection`.
 *
 * - **The projection** (`ayni:plate-carree`): lon/lat as they are. Tile
 *   0/0/0 is 360° wide with its top-left corner at (−180°, 90°), so the
 *   Earth fills the square's top half and a flat zoom shows the globe's
 *   scale at the equator: centre and zoom mean the same in both, for rooms,
 *   Khipus, links and the multi-globe camera sync.
 * - **GeoJSON** is projected in MapLibre's workers, by a projection handler
 *   loaded into them here (the identity, like the projection).
 * - **The Earth under it** comes from GIBS's EPSG:4326 tiles. Their grid
 *   starts at 288° tiles, not 360°, so the `ayni-gibs4326://` protocol cuts
 *   each map tile out of the GIBS tiles it overlaps. Both grids are plate
 *   carrée, so that is plain cropping, no reprojection.
 */

import * as maplibregl from 'maplibre-gl'

/** The projection's name, in `map.setProjection({ type })`. */
export const FLAT_PROJECTION = 'ayni:plate-carree'
/** Degrees across tile 0/0/0. */
export const FLAT_EXTENT = 360
/** The protocol that serves GIBS's EPSG:4326 imagery on the flat map's tile grid. */
export const GIBS_4326_PROTOCOL = 'ayni-gibs4326'
/** Map tiles of this size in CSS px (the flat Earth's raster source `tileSize`). */
export const FLAT_TILE_SIZE = 256

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v))
/**
 * Lon/lat as they are. The inverse keeps to the Earth: the square's lower
 * half (south of −90°) answers with the edge, so a point there does not
 * round-trip and reads as off the map (MapRenderer's on-globe test), and
 * MapLibre never builds a LngLat past the pole.
 */
export const plateCarree = {
  forward: (p: number[]): number[] => [p[0], p[1]],
  inverse: (p: number[]): number[] => [clamp(p[0], -180, 180), clamp(p[1], -90, 90)],
}
const HANDLER = 'self.setProjectionHandler(() => ({ forward: (p) => [p[0], p[1]], inverse: (p) => [Math.max(-180, Math.min(180, p[0])), Math.max(-90, Math.min(90, p[1]))] }))'

let registered: Promise<void> | null = null

/**
 * Register the projection, its workers' handler and the GIBS protocol, once
 * per page. Resolves when the workers have the handler: GeoJSON loaded on a
 * flat map before that is drawn in the wrong place.
 */
export function registerFlatProjection(): Promise<void> {
  if (registered) return registered
  registered = (async () => {
    maplibregl.addProjection({
      name: FLAT_PROJECTION,
      definition: FLAT_PROJECTION,
      projection: plateCarree,
      tileMatrixSet: { origin: [-180, 90], extentAtZoom0: FLAT_EXTENT },
    })
    maplibregl.addProtocol(GIBS_4326_PROTOCOL, loadGibs4326Tile)
    maplibregl.addProtocol(MERCATOR_ON_FLAT_PROTOCOL, loadMercatorOnFlatTile)
    const url = URL.createObjectURL(new Blob([HANDLER], { type: 'text/javascript' }))
    try {
      await maplibregl.importScriptInWorkers(url)
    } finally {
      URL.revokeObjectURL(url)
    }
  })()
  return registered
}

/** The lon/lat box of a flat map tile: west, north and its side in degrees. */
export function flatTileBox(z: number, x: number, y: number): { west: number; north: number; span: number } {
  const span = FLAT_EXTENT / 2 ** z
  return { west: -180 + x * span, north: 90 - y * span, span }
}

/** GIBS's EPSG:4326 grid: level 0 tiles are 288° wide, 512 px, from (−180°, 90°). */
const GIBS_SPAN0 = 288
const GIBS_TILE_PX = 512

/** The GIBS level whose pixels are at least as fine as a flat map tile's at zoom `z`. */
export function gibsLevelFor(z: number, maxLevel: number): number {
  // A map tile has FLAT_TILE_SIZE px over 360/2^z degrees; a GIBS tile 512 px over 288/2^L.
  const need = Math.log2((FLAT_TILE_SIZE * GIBS_SPAN0) / (GIBS_TILE_PX * FLAT_EXTENT)) + z
  return Math.max(0, Math.min(maxLevel, Math.ceil(need - 1e-9)))
}

/**
 * The GIBS tiles (level, row, column) a flat map tile overlaps, and where
 * each one goes on it, in the map tile's pixels.
 */
export function gibsTilesFor(z: number, x: number, y: number, maxLevel: number): { level: number; row: number; col: number; dx: number; dy: number; size: number }[] {
  const { west, north, span } = flatTileBox(z, x, y)
  const level = gibsLevelFor(z, maxLevel)
  const s = GIBS_SPAN0 / 2 ** level
  const cols = Math.ceil(360 / s)
  const rows = Math.ceil(180 / s)
  const px = FLAT_TILE_SIZE / span
  const south = Math.max(-90, north - span)
  const east = Math.min(180, west + span)
  if (south >= north || east <= west) return []
  const out = []
  const c0 = Math.max(0, Math.floor((west + 180) / s))
  const c1 = Math.min(cols - 1, Math.floor((east + 180 - 1e-9) / s))
  const r0 = Math.max(0, Math.floor((90 - north) / s))
  const r1 = Math.min(rows - 1, Math.floor((90 - south - 1e-9) / s))
  for (let row = r0; row <= r1; row++) {
    for (let col = c0; col <= c1; col++) {
      out.push({ level, row, col, dx: (col * s - 180 - west) * px, dy: (north - (90 - row * s)) * px, size: s * px })
    }
  }
  return out
}

/** One imagery layer behind the protocol: `ayni-gibs4326://<layer>/<time>/<matrix>/<ext>/<maxLevel>/{z}/{x}/{y}`. */
interface GibsLayerUrl {
  layer: string
  time: string
  matrix: string
  ext: string
  maxLevel: number
  z: number
  x: number
  y: number
}

export function parseGibs4326Url(url: string): GibsLayerUrl | null {
  const m = /^ayni-gibs4326:\/\/([A-Za-z0-9_]+)\/([0-9-]+)\/([0-9a-z]+)\/(jpg|png)\/(\d+)\/(\d+)\/(\d+)\/(\d+)$/.exec(url)
  if (!m) return null
  return { layer: m[1], time: m[2], matrix: m[3], ext: m[4], maxLevel: +m[5], z: +m[6], x: +m[7], y: +m[8] }
}

/** Through our tile proxy (`functions/api/tile/`), which caches GIBS at the edge. */
export const gibs4326TileUrl = (u: Pick<GibsLayerUrl, 'layer' | 'time' | 'matrix' | 'ext'>, level: number, row: number, col: number): string =>
  `/api/tile/epsg4326/${u.layer}/default/${u.time}/${u.matrix}/${level}/${row}/${col}.${u.ext === 'jpg' ? 'jpeg' : 'png'}`

/** Decoded GIBS tiles: neighbouring map tiles share them (so no one map tile's cancelling stops a fetch). */
const cache = new Map<string, Promise<ImageBitmap | null>>()
const CACHE_SIZE = 96

/** Waits before the second and third tries: GIBS refuses bursts now and then (403/429), and a moment later serves them. */
const RETRY_MS = [400, 1500]

async function fetchWithRetry(url: string): Promise<Blob | null> {
  for (let attempt = 0; ; attempt++) {
    const r = await fetch(url)
    if (r.ok) return r.blob()
    if (r.status === 404 || attempt >= RETRY_MS.length) return null
    await new Promise((resolve) => setTimeout(resolve, RETRY_MS[attempt]))
  }
}

function gibsTile(url: string): Promise<ImageBitmap | null> {
  const hit = cache.get(url)
  if (hit) {
    cache.delete(url)
    cache.set(url, hit)
    return hit
  }
  const p = fetchWithRetry(url)
    .then((b) => (b ? createImageBitmap(b) : null))
    .catch(() => null)
    .then((bmp) => {
      // A tile that did not come is asked for again next time, not remembered as missing.
      if (!bmp) cache.delete(url)
      return bmp
    })
  cache.set(url, p)
  while (cache.size > CACHE_SIZE) {
    const oldest = cache.keys().next().value as string
    void cache.get(oldest)?.then((bmp) => bmp?.close())
    cache.delete(oldest)
  }
  return p
}

async function loadGibs4326Tile(params: { url: string }): Promise<{ data: ArrayBuffer }> {
  const u = parseGibs4326Url(params.url)
  if (!u) throw new Error(`Bad flat tile URL ${params.url}`)
  const canvas = new OffscreenCanvas(FLAT_TILE_SIZE, FLAT_TILE_SIZE)
  const g = canvas.getContext('2d')
  if (!g) throw new Error('No 2D context')
  g.imageSmoothingEnabled = true
  g.imageSmoothingQuality = 'high'
  const parts = gibsTilesFor(u.z, u.x, u.y, u.maxLevel)
  const images = await Promise.all(parts.map((p) => gibsTile(gibs4326TileUrl(u, p.level, p.row, p.col))))
  // A part that did not come fails the tile, so MapLibre asks again later rather than keeping a hole.
  if (images.some((img) => !img)) throw new Error(`GIBS tile missing for ${params.url}`)
  parts.forEach((p, i) => g.drawImage(images[i]!, p.dx, p.dy, p.size, p.size))
  const blob = await canvas.convertToBlob({ type: u.ext === 'jpg' ? 'image/jpeg' : 'image/png', quality: 0.92 })
  return { data: await blob.arrayBuffer() }
}

// --- Web Mercator tiles on the flat map (the city close-up's photos) ---

/** The protocol that serves any Web Mercator XYZ raster on the flat map's tile grid. */
export const MERCATOR_ON_FLAT_PROTOCOL = 'ayni-merc2pc'

/**
 * A Web Mercator source's tiles on the flat map: `template` is its `{z}/{x}/{y}`
 * URL; `minzoom`/`maxzoom` the levels it has (deeper flat tiles are cut from
 * the deepest, larger).
 */
export function mercatorOnFlatUrl(template: string, minzoom: number, maxzoom: number): string {
  return `${MERCATOR_ON_FLAT_PROTOCOL}://tile/{z}/{x}/{y}?min=${minzoom}&max=${maxzoom}&t=${encodeURIComponent(template)}`
}

export function parseMercatorOnFlatUrl(url: string): { z: number; x: number; y: number; min: number; max: number; template: string } | null {
  const m = /^ayni-merc2pc:\/\/tile\/(\d+)\/(\d+)\/(\d+)\?min=(\d+)&max=(\d+)&t=([^&]+)$/.exec(url)
  if (!m) return null
  const template = decodeURIComponent(m[6])
  // Our own tiles only (this site's, as a path or with its origin): never another host.
  const origin = globalThis.location?.origin ?? ''
  const own = (template.startsWith('/') && !template.startsWith('//')) || (origin !== '' && template.startsWith(`${origin}/`))
  if (!own) return null
  return { z: +m[1], x: +m[2], y: +m[3], min: +m[4], max: +m[5], template }
}

const MERC_TILE = 256
/** Web Mercator y in [0, 1] (north at 0) of a latitude. */
export const mercatorY = (lat: number): number => {
  const s = Math.sin(clamp(lat, -85.05112878, 85.05112878) * Math.PI / 180)
  return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)
}

/**
 * How to cut a flat map tile out of a Web Mercator source: the level (as
 * fine across as the flat tile, which is as fine as the flat tile up and
 * down too away from the equator), the tiles it overlaps, and for each strip
 * of output rows the source rows to stretch onto it. Mercator and plate
 * carrée share their longitudes, so across is a plain scale; up and down
 * each strip has its own (8 rows: the curve within one is under a tenth of
 * a pixel at the flat map's zooms).
 */
export function mercatorCut(z: number, x: number, y: number, minzoom: number, maxzoom: number): {
  level: number
  tiles: { tx: number; ty: number }[]
  /** Pixel origin of the tile block in the level's world pixels. */
  originX: number
  originY: number
  /** Source x of the output's left edge and its width, in the block's pixels. */
  sx: number
  sw: number
  strips: { row: number; rows: number; sy: number; sh: number }[]
} | null {
  const { west, north, span } = flatTileBox(z, x, y)
  const south = Math.max(-85.05112878, north - span)
  const top = Math.min(85.05112878, north)
  if (south >= top) return null
  const level = clamp(z, minzoom, maxzoom)
  const world = MERC_TILE * 2 ** level
  const px = (lon: number) => ((lon + 180) / 360) * world
  const py = (lat: number) => mercatorY(lat) * world
  const x0 = px(west), x1 = px(Math.min(180, west + span))
  const y0 = py(top), y1 = py(south)
  const n = 2 ** level
  const tx0 = clamp(Math.floor(x0 / MERC_TILE), 0, n - 1), tx1 = clamp(Math.floor((x1 - 1e-6) / MERC_TILE), 0, n - 1)
  const ty0 = clamp(Math.floor(y0 / MERC_TILE), 0, n - 1), ty1 = clamp(Math.floor((y1 - 1e-6) / MERC_TILE), 0, n - 1)
  const tiles = []
  for (let ty = ty0; ty <= ty1; ty++) for (let tx = tx0; tx <= tx1; tx++) tiles.push({ tx, ty })
  const originX = tx0 * MERC_TILE, originY = ty0 * MERC_TILE
  const STRIP = 8
  const strips = []
  const rowsTo = (r: number) => north - (r / FLAT_TILE_SIZE) * span
  for (let row = 0; row < FLAT_TILE_SIZE; row += STRIP) {
    const a = rowsTo(row), b = rowsTo(row + STRIP)
    if (b >= top || a <= south) continue
    const ra = Math.max(row, Math.ceil(((north - top) / span) * FLAT_TILE_SIZE))
    const rb = Math.min(row + STRIP, Math.floor(((north - south) / span) * FLAT_TILE_SIZE))
    if (rb <= ra) continue
    const sy = py(rowsTo(ra)) - originY
    strips.push({ row: ra, rows: rb - ra, sy, sh: py(rowsTo(rb)) - originY - sy })
  }
  return { level, tiles, originX, originY, sx: x0 - originX, sw: x1 - x0, strips }
}

/** One Web Mercator tile: the picture, `missing` (404: nothing there), or null (it did not come). */
async function mercatorTile(url: string): Promise<ImageBitmap | 'missing' | null> {
  for (let attempt = 0; ; attempt++) {
    try {
      const r = await fetch(url)
      if (r.status === 404 || r.status === 204) return 'missing'
      if (r.ok) return await createImageBitmap(await r.blob())
    } catch {
      // network: try again
    }
    if (attempt >= RETRY_MS.length) return null
    await new Promise((resolve) => setTimeout(resolve, RETRY_MS[attempt]))
  }
}

async function loadMercatorOnFlatTile(params: { url: string }): Promise<{ data: ArrayBuffer }> {
  const u = parseMercatorOnFlatUrl(params.url)
  if (!u) throw new Error(`Bad flat tile URL ${params.url}`)
  const canvas = new OffscreenCanvas(FLAT_TILE_SIZE, FLAT_TILE_SIZE)
  const g = canvas.getContext('2d')
  if (!g) throw new Error('No 2D context')
  const cut = mercatorCut(u.z, u.x, u.y, u.min, u.max)
  if (cut) {
    const fill = (t: string, v: number) => t.replace(`{${'z'}}`, String(cut.level)).replace(`{${'x'}}`, String(v))
    const images = await Promise.all(cut.tiles.map(({ tx, ty }) => mercatorTile(fill(u.template, tx).replace(`{${'y'}}`, String(ty)))))
    if (images.some((img) => img === null)) throw new Error(`Map tile missing for ${params.url}`)
    if (images.some((img) => img !== 'missing')) {
      // The block of source tiles, then each strip of rows stretched from it.
      const cols = new Set(cut.tiles.map((t) => t.tx)).size
      const rows = cut.tiles.length / cols
      const block = new OffscreenCanvas(cols * MERC_TILE, rows * MERC_TILE)
      const bg = block.getContext('2d')
      if (!bg) throw new Error('No 2D context')
      cut.tiles.forEach((t, i) => {
        const img = images[i]
        if (img && img !== 'missing') bg.drawImage(img, t.tx * MERC_TILE - cut.originX, t.ty * MERC_TILE - cut.originY)
      })
      g.imageSmoothingEnabled = true
      g.imageSmoothingQuality = 'high'
      for (const s of cut.strips) g.drawImage(block, cut.sx, s.sy, cut.sw, s.sh, 0, s.row, FLAT_TILE_SIZE, s.rows)
    }
  }
  const blob = await canvas.convertToBlob({ type: 'image/webp', quality: 0.9 })
  return { data: await blob.arrayBuffer() }
}
